import Dialog from "@mui/material/Dialog";
import { type FormEvent, useState } from "react";
import { GfgButton } from "@/components/ui/GfgButton";
import { GfgTextField } from "@/components/ui/GfgTextField";
import { useChangeUserEmail } from "@/hooks/useUsers";
import type { ChangeEmailResponse, User } from "@/interfaces/user";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { validateNewEmail } from "@/utils/userRowActions-helper";

interface ChangeEmailDialogProps {
  user: User | null;
  onClose: () => void;
  onChanged: (response: ChangeEmailResponse) => void;
}

const TITLE_ID = "change-email-title";

interface ChangeEmailFormProps {
  user: User;
  changeEmail: ReturnType<typeof useChangeUserEmail>;
  onClose: () => void;
  onChanged: (response: ChangeEmailResponse) => void;
}

function ChangeEmailForm({ user, changeEmail, onClose, onChanged }: ChangeEmailFormProps) {
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const apiError = changeEmail.isError
    ? apiErrorMessage(changeEmail.error, "Couldn't change the email. Try again.")
    : null;
  const errorText = validationError ?? apiError;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (changeEmail.isPending) return;
    const problem = validateNewEmail(email, user.email);
    setValidationError(problem);
    if (problem) return;
    changeEmail.mutate(
      { userId: user.id, email: email.trim() },
      {
        onSuccess: (response) => {
          onChanged(response);
          onClose();
        },
      },
    );
  }

  return (
    <form
      aria-labelledby={TITLE_ID}
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-4 p-5"
    >
      <h2 id={TITLE_ID} className="font-crimson text-[30px] text-rodeo-black">
        Change Email
      </h2>
      <p className="font-lato text-sm text-graphite">
        Current email: <span className="font-bold text-rodeo-black">{user.email}</span>
      </p>
      <GfgTextField
        label="New email"
        type="email"
        autoComplete="off"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          setValidationError(null);
          if (changeEmail.isError) changeEmail.reset();
        }}
        error={Boolean(errorText)}
        errorText={errorText ?? undefined}
      />
      <p className="font-lato text-sm text-graphite">
        Both the old and the new address will get a notification.
      </p>
      <div className="mt-2 flex items-center justify-end gap-4">
        <GfgButton
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={changeEmail.isPending}
        >
          Cancel
        </GfgButton>
        <GfgButton type="submit" disabled={changeEmail.isPending}>
          Change Email
        </GfgButton>
      </div>
    </form>
  );
}

function focusNewEmailInput(node: HTMLElement) {
  node.querySelector("input")?.focus();
}

export function ChangeEmailDialog({ user, onClose, onChanged }: ChangeEmailDialogProps) {
  const changeEmail = useChangeUserEmail();
  if (!user) return null;
  function close() {
    changeEmail.reset();
    onClose();
  }
  return (
    <Dialog
      open
      onClose={() => {
        if (!changeEmail.isPending) close();
      }}
      aria-labelledby={TITLE_ID}
      slotProps={{ transition: { onEntered: focusNewEmailInput } }}
      fullWidth
      maxWidth="xs"
    >
      <ChangeEmailForm
        key={user.id}
        user={user}
        changeEmail={changeEmail}
        onClose={close}
        onChanged={onChanged}
      />
    </Dialog>
  );
}

export default ChangeEmailDialog;
